import React, { useEffect, useState } from "react";
import { Copy } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { fmtCurrency, fmtDate } from "@/utils/format";

export function SimilarQuoteBadge({ similar, onOpen, testid }) {
  if (!similar?.length) return null;
  const top = similar[0].ratio;
  const nums = similar.map((s) => s.quote_number).join(", ");
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(); }} data-testid={testid}
      title={`Benzer teklifler: ${similar.map((s) => `${s.quote_number} (${s.customer_name}, %${s.ratio})`).join(" · ")}`}
      className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 text-[10px] font-mono uppercase border-2 border-[#7C3AED] text-[#7C3AED] bg-[#7C3AED]/10 hover:bg-[#7C3AED] hover:text-white transition-colors max-w-[260px] text-left">
      <Copy size={10} className="shrink-0" />
      <span className="truncate">Benzer teklif · %{top} aynı · {nums}</span>
    </button>
  );
}

const KIND = {
  order: { label: "Sipariş", cls: "bg-[#0055FF] text-white border-[#0055FF]" },
  quote: { label: "Teklif", cls: "bg-neutral-100 text-neutral-800 border-neutral-400" },
};

function QuoteHead({ q, isBase }) {
  const k = KIND[q.kind];
  return (
    <TableHead className={`align-top py-2 min-w-[150px] ${isBase ? "bg-[#7C3AED]/10" : ""}`} data-testid={`similar-col-${q.id}`}>
      <div className="font-mono font-bold text-xs text-[#09090B]">{q.quote_number}{isBase && <span className="ml-1 text-[9px] text-[#7C3AED]">(bu teklif)</span>}</div>
      <div className="text-xs font-semibold text-[#09090B] normal-case">{q.customer_name}</div>
      <div className="text-[10px] font-mono text-neutral-500">{fmtDate(q.created_at)}{q.ratio != null && ` · %${q.ratio} aynı`}</div>
      <span className={`inline-block mt-1 px-1.5 py-0.5 text-[9px] font-mono uppercase border ${k.cls}`}>{k.label}</span>
    </TableHead>
  );
}

export function SimilarQuotesDialog({ quoteId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!quoteId) { setData(null); return; }
    api.get(`/quotes/${quoteId}/similar?_ts=${Date.now()}`)
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [quoteId]);

  return (
    <Dialog open={!!quoteId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-none border-2 border-[#09090B] bg-white sm:max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="similar-quotes-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">Benzer Teklif Karşılaştırması</DialogTitle>
        </DialogHeader>
        {!data ? <div className="p-8 text-center text-sm font-mono text-neutral-500">Yükleniyor…</div> : (
          <div className="overflow-x-auto border-2 border-[#09090B]">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
                  <TableHead className="font-mono uppercase text-xs align-top py-2 min-w-[200px]">Ortak Kalem (Ürün · Adet)</TableHead>
                  {data.quotes.map((q) => <QuoteHead key={q.id} q={q} isBase={q.id === data.base_id} />)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.products.map((p) => {
                  const vals = Object.values(p.prices).filter((v) => v != null && v > 0);
                  const min = Math.min(...vals);
                  return (
                    <TableRow key={p.product_id} data-testid={`similar-row-${p.product_id}`}>
                      <TableCell className="text-sm font-semibold">
                        {p.product_name}
                        <div className="text-[10px] font-mono text-neutral-500 font-normal">Adet: {p.quantity} {p.unit}</div>
                      </TableCell>
                      {data.quotes.map((q) => {
                        const v = p.prices[q.id];
                        const best = v != null && v === min && vals.length > 1;
                        return (
                          <TableCell key={q.id} className={`font-mono text-sm ${q.id === data.base_id ? "bg-[#7C3AED]/5" : ""}`}
                            data-testid={`similar-price-${p.product_id}-${q.id}`}>
                            {v == null ? <span className="text-neutral-300">—</span>
                              : (
                                <div className="leading-tight">
                                  <span className={best ? "font-bold text-[#00A650]" : ""} title={best ? "En düşük fiyat" : undefined}>{fmtCurrency(v)}</span>
                                  <div className="text-[10px] text-neutral-500">{p.quantities?.[q.id]} adet</div>
                                </div>
                              )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                {data.products.length === 0 && (
                  <TableRow><TableCell colSpan={data.quotes.length + 1} className="text-center py-8 text-neutral-500">Benzer teklif bulunamadı</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
        <div className="text-[10px] font-mono text-neutral-500">Yeşil: ortak kalemde en düşük birim fiyat · Ortak kalem = aynı ürün ve aynı adet · Oran = ortak kalem / teklifteki toplam kalem · Yalnızca en fazla 5 gün arayla verilmiş, farklı müşterilere ait teklifler karşılaştırılır.</div>
      </DialogContent>
    </Dialog>
  );
}
